import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) throw new Error('usePermissions must be used within PermissionsProvider');
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/permissions/me`);
      setPermissions(res.data);
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const hasPage = (pageKey) => {
    if (!permissions) return true;
    return permissions.pages?.[pageKey] === true;
  };

  const hasAction = (actionKey) => {
    if (!permissions) return false;
    return permissions.actions?.[actionKey] === true;
  };

  const hasModule = (moduleKey) => {
    if (!permissions) return true;
    return permissions.modules?.[moduleKey] === true;
  };

  const refresh = () => fetchPermissions();

  return (
    <PermissionsContext.Provider value={{ permissions, loading, hasPage, hasAction, hasModule, refresh }}>
      {children}
    </PermissionsContext.Provider>
  );
};
